import React, { useEffect, useState } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { locationService } from '../../../shared/services/locationService';
import { LocationOption } from '../../../shared/types/location';

export interface ClientFilterValues {
    country: string;
    state: string;
    city: string;
    status: 'all' | 'active' | 'inactive';
}

const EMPTY_FILTERS: ClientFilterValues = {
    country: '',
    state: '',
    city: '',
    status: 'all',
};

interface ClientFiltersProps {
    onApply: (filters: ClientFilterValues) => void;
    onReset: () => void;
}

export const ClientFilters: React.FC<ClientFiltersProps> = ({
    onApply,
    onReset,
}) => {
    const [filters, setFilters] = useState<ClientFilterValues>(EMPTY_FILTERS);
    const [countries, setCountries] = useState<LocationOption[]>([]);
    const [states, setStates] = useState<LocationOption[]>([]);
    const [cities, setCities] = useState<LocationOption[]>([]);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [selectedStateId, setSelectedStateId] = useState('');
    const [selectedCityId, setSelectedCityId] = useState('');

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const countryList = await locationService.getCountries();
                setCountries(countryList);
            } catch {
                setCountries([]);
            }
        };
        loadCountries();
    }, []);

    const handleCountryChange = async (countryId: string) => {
        const selectedCountry = countries.find((item) => item.id === countryId);
        setSelectedCountryId(countryId);
        setSelectedStateId('');
        setSelectedCityId('');
        setStates([]);
        setCities([]);
        setFilters((prev) => ({
            ...prev,
            country: selectedCountry?.name || '',
            state: '',
            city: '',
        }));

        if (!countryId) return;
        const stateList = await locationService.getStatesByCountry(countryId);
        setStates(stateList);
    };

    const handleStateChange = async (stateId: string) => {
        const selectedState = states.find((item) => item.id === stateId);
        setSelectedStateId(stateId);
        setSelectedCityId('');
        setCities([]);
        setFilters((prev) => ({
            ...prev,
            state: selectedState?.name || '',
            city: '',
        }));

        if (!stateId) return;
        const cityList = await locationService.getCitiesByState(stateId);
        setCities(cityList);
    };

    const handleCityChange = (cityId: string) => {
        const selectedCity = cities.find((item) => item.id === cityId);
        setSelectedCityId(cityId);
        setFilters((prev) => ({
            ...prev,
            city: selectedCity?.name || '',
        }));
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setSelectedCountryId('');
        setSelectedStateId('');
        setSelectedCityId('');
        setStates([]);
        setCities([]);
        onReset();
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5 mb-6">
            <div className="mb-4">
                <p className="text-xs font-bold uppercase tracking-widest text-[#6366f1]">Filters</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextField
                    select
                    label="Country"
                    value={selectedCountryId}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    placeholder="Filter By Country"
                    fullWidth
                    size="small"
                >
                    <MenuItem value="">All Countries</MenuItem>
                    {countries.map((item) => (
                        <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    label="State"
                    value={selectedStateId}
                    onChange={(e) => handleStateChange(e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!selectedCountryId}
                >
                    <MenuItem value="">All States</MenuItem>
                    {states.map((item) => (
                        <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    label="City"
                    value={selectedCityId}
                    onChange={(e) => handleCityChange(e.target.value)}
                    fullWidth
                    size="small"
                    disabled={!selectedStateId}
                >
                    <MenuItem value="">All Cities</MenuItem>
                    {cities.map((item) => (
                        <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                    ))}
                </TextField>



                <TextField
                    select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => setFilters((prev) => ({
                        ...prev,
                        status: e.target.value as ClientFilterValues['status'],
                    }))}
                    fullWidth
                    size="small"
                >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
            </div>

            <div className="flex gap-2 mt-4">
                <AppButton variant="primary" onClick={() => onApply(filters)} sx={{ px: 3, py: 1, borderRadius: '6px' }}>
                    Search
                </AppButton>
                <AppButton variant="secondary" onClick={handleReset} sx={{ px: 3, py: 1, borderRadius: '6px' }}>
                    Reset
                </AppButton>
            </div>
        </div>
    );
};
